import { useState } from 'react';
import { useUnifiedMetrics, formatCurrency } from '@/hooks/useUnifiedMetrics';
import { useLeads } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { useWorkspace } from '@/hooks/useWorkspace';
import { generatePriorityActions } from '@/lib/decisionEngine';
import { detectQuestionType, generateContextualResponse } from '@/lib/aiResponseEngine';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Sparkles,
  Zap,
  AlertTriangle,
  Target,
  MessageCircle,
  Phone,
  Mail,
  ChevronRight,
  RefreshCw,
  Copy,
  Check,
  Send,
  Bot,
  User,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
  structured?: AIStructuredResponse;
  timestamp: Date;
}

interface PriorityAction {
  lead_id: string;
  lead_name: string;
  title: string;
  why: string;
  eta_minutes?: number;
  recommended_action: 'call' | 'whatsapp' | 'email' | 'meeting';
  message_draft?: string;
  confidence: 'high' | 'medium' | 'low';
}

interface Alert {
  type: 'sla_breach' | 'stale_lead' | 'missing_value' | 'pipeline_gap';
  message: string;
  severity: 'high' | 'medium' | 'low';
  lead_id?: string;
}

interface AIStructuredResponse {
  daily_brief: string;
  priority_actions: PriorityAction[];
  alerts: Alert[];
  proposed_updates?: Array<{
    lead_id: string;
    lead_name?: string;
    updates: Record<string, unknown>;
    reason: string;
    requires_confirmation: boolean;
  }>;
  questions?: string[];
}

export function AIPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { workspace, loading: workspaceLoading } = useWorkspace();
  const { metrics } = useUnifiedMetrics();
  const { data: leads } = useLeads();
  const { data: deals } = useDeals();

  const generateBrief = async (prompt?: string) => {
    if (workspaceLoading) {
      toast.info('Loading workspace...');
      return;
    }
    
    if (!workspace?.id) {
      toast.error('Workspace not found. Please refresh the page.');
      return;
    }

    setIsGenerating(true);
    
    // Add user message if prompt provided
    if (prompt) {
      setMessages(prev => [...prev, {
        role: 'user',
        content: prompt,
        timestamp: new Date()
      }]);
    }

    // Use local generation with contextual responses
    const response = generateLocalBrief(prompt || 'What should I focus on today?');
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: response.daily_brief,
      structured: response,
      timestamp: new Date()
    }]);
    
    setIsGenerating(false);
  };

  // Local fallback generation with contextual responses
  const generateLocalBrief = (prompt: string = 'What should I focus on today?'): AIStructuredResponse => {
    if (!leads || !deals || !metrics) {
      return {
        daily_brief: 'No data available to generate brief.',
        priority_actions: [],
        alerts: []
      };
    }

    const openLeads = leads.filter(l => 
      l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost'
    );
    const dealsList = deals.filter(d => d.stage !== 'won' && d.stage !== 'lost')
      .map(d => ({ id: d.id, lead_id: d.lead_id, value: d.value, stage: d.stage }));
    
    const actions = generatePriorityActions(openLeads, dealsList, 5);

    const priorityActions: PriorityAction[] = actions.map(action => ({
      lead_id: action.leadId,
      lead_name: action.lead.name,
      title: action.title,
      why: action.reasons[0] || 'High-priority based on score',
      eta_minutes: 10,
      recommended_action: action.recommendedAction,
      message_draft: action.suggestedMessage,
      confidence: action.score >= 70 ? 'high' : action.score >= 45 ? 'medium' : 'low'
    }));

    const alerts: Alert[] = [];
    
    // Check for missing values on won deals
    const wonNoValue = deals.filter(d => d.stage === 'won' && !d.value);
    if (wonNoValue.length > 0) {
      alerts.push({
        type: 'missing_value',
        message: `${wonNoValue.length} won deal(s) missing value`,
        severity: 'medium'
      });
    }

    // Check for overdue follow-ups
    if (metrics.overdueFollowups > 0) {
      alerts.push({
        type: 'sla_breach',
        message: `${metrics.overdueFollowups} overdue follow-up(s)`,
        severity: 'high'
      });
    }

    // Check for stale leads
    if (metrics.stuckDeals > 0) {
      alerts.push({
        type: 'stale_lead',
        message: `${metrics.stuckDeals} stale deal(s) need attention`,
        severity: 'medium'
      });
    }

    // Use contextual AI response engine
    const questionType = detectQuestionType(prompt);
    const contextualResponse = generateContextualResponse(questionType, {
      leads: openLeads,
      deals: dealsList,
      metrics: {
        pipelineValue: metrics.pipelineValue,
        needAction: metrics.needAction,
        overdueFollowups: metrics.overdueFollowups,
        stuckDeals: metrics.stuckDeals,
        winRate: metrics.winRate || 0,
        forecast: metrics.forecast || 0
      },
      priorityActions
    });

    return {
      daily_brief: contextualResponse.daily_brief,
      priority_actions: priorityActions,
      alerts
    };
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    const prompt = inputValue;
    setInputValue('');
    await generateBrief(prompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const copyMessage = async (message: string, id: string) => {
    await navigator.clipboard.writeText(message);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'call': return Phone;
      case 'whatsapp': return MessageCircle;
      case 'email': return Mail;
      default: return MessageCircle;
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'border-success/50 text-success bg-success/10';
      case 'medium': return 'border-warning/50 text-warning bg-warning/10';
      case 'low': return 'border-muted text-muted-foreground bg-muted/50';
      default: return 'border-muted text-muted-foreground bg-muted/50';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-destructive/10 border-destructive/30 text-destructive';
      case 'medium': return 'bg-warning/10 border-warning/30 text-warning';
      case 'low': return 'bg-muted border-border text-muted-foreground';
      default: return 'bg-muted border-border text-muted-foreground';
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/20 hover:border-primary/40"
        >
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="hidden sm:inline">AI Brief</span>
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="px-4 py-3 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-base">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              Decision Sales Manager
            </SheetTitle>
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">Elite</Badge>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            {messages.length === 0 && !isGenerating && (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Ready to assist</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
                  I'll analyze your pipeline and suggest the highest-impact actions for today.
                </p>
                <Button onClick={() => generateBrief()} className="gap-2">
                  <Zap className="w-4 h-4" />
                  Generate Daily Brief
                </Button>
              </div>
            )}

            {messages.map((message, index) => (
              <div key={index} className="space-y-3">
                {/* Message bubble */}
                <div className={cn(
                  "flex gap-3",
                  message.role === 'user' ? "justify-end" : "justify-start"
                )}>
                  {message.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                  )}
                  <div className={cn(
                    "max-w-[85%] rounded-xl px-4 py-2.5 text-sm",
                    message.role === 'user' 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-secondary text-foreground"
                  )}>
                    {message.content}
                  </div>
                  {message.role === 'user' && (
                    <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Structured response */}
                {message.structured && (
                  <div className="space-y-4 ml-10">
                    {/* Priority Actions */}
                    {message.structured.priority_actions.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          <Target className="w-3.5 h-3.5" />
                          Priority Actions
                        </div>
                        {message.structured.priority_actions.map((action, i) => {
                          const ActionIcon = getActionIcon(action.recommended_action);
                          return (
                            <div 
                              key={i}
                              className="p-3 rounded-lg border bg-card hover:border-primary/30 transition-colors"
                            >
                              <div className="flex items-start gap-3">
                                <div className={cn(
                                  "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                                  i === 0 ? "bg-primary text-primary-foreground" :
                                  i === 1 ? "bg-primary/60 text-primary-foreground" :
                                  "bg-muted text-muted-foreground"
                                )}>
                                  {i + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Link 
                                      to={`/app/leads?id=${action.lead_id}`}
                                      className="font-medium text-sm hover:text-primary transition-colors"
                                    >
                                      {action.lead_name}
                                    </Link>
                                    <Badge 
                                      variant="outline" 
                                      className={cn("text-[10px]", getConfidenceColor(action.confidence))}
                                    >
                                      {action.confidence}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground mb-2">{action.why}</p>
                                  <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5">
                                      <ActionIcon className="w-3 h-3" />
                                      {action.recommended_action}
                                    </Button>
                                    {action.eta_minutes && (
                                      <span className="text-[10px] text-muted-foreground">
                                        ~{action.eta_minutes} min
                                      </span>
                                    )}
                                  </div>
                                  {action.message_draft && (
                                    <div className="mt-2 p-2 rounded bg-muted/50 text-xs">
                                      <div className="flex items-start justify-between gap-2">
                                        <p className="text-muted-foreground italic line-clamp-2">
                                          "{action.message_draft}"
                                        </p>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 shrink-0"
                                          onClick={() => copyMessage(action.message_draft!, `${index}-${i}`)}
                                        >
                                          {copiedId === `${index}-${i}` ? (
                                            <Check className="w-3 h-3 text-success" />
                                          ) : (
                                            <Copy className="w-3 h-3" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Alerts */}
                    {message.structured.alerts.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Alerts
                        </div>
                        {message.structured.alerts.map((alert, i) => (
                          <div 
                            key={i}
                            className={cn(
                              "p-3 rounded-lg border text-sm",
                              getSeverityColor(alert.severity)
                            )}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <p className="font-medium text-xs uppercase tracking-wide mb-0.5">
                                  {alert.type.replace('_', ' ')}
                                </p>
                                <p className="text-sm">{alert.message}</p>
                              </div>
                              {alert.lead_id && (
                                <Link to={`/app/leads?id=${alert.lead_id}`}>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {isGenerating && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <RefreshCw className="w-4 h-4 text-primary animate-spin" />
                </div>
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input area */}
        <div className="p-4 border-t bg-card/50">
          {messages.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => generateBrief()}
              disabled={isGenerating}
              className="w-full mb-3 text-xs text-muted-foreground hover:text-foreground"
            >
              <RefreshCw className={cn("w-3.5 h-3.5 mr-1.5", isGenerating && "animate-spin")} />
              Regenerate Brief
            </Button>
          )}
          <div className="relative">
            <Input
              placeholder="Ask anything about your pipeline..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isGenerating}
              className="pr-10"
            />
            <Button 
              size="sm" 
              variant="ghost" 
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={handleSend}
              disabled={!inputValue.trim() || isGenerating}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
